/**
 * Object-level bulk actions for the unified drawer (Evo/PSC only) - the two
 * object-wide buttons on a slim, left-aligned row directly beneath the tabs
 * while the Open issues tab is active. No heading: the buttons label
 * themselves. Both are outlined (no filled primary); only their icons carry
 * colour, previewing the outcome state - teal AssignmentTurnedIn for "Fixed all
 * N" (marks every open sibling fixed, AWAITING sync confirmation - never green,
 * that's reserved for sync-confirmed resolved), blocked-red pause for "Can't
 * fix yet" (blocks the un-blocked open rows).
 */
import React from 'react'
import { Box, Button } from '@mui/material'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { poolwerxColors } from '../../../../theme/colors'
import {
  BLOCKED_FG,
  MARKED_FIXED_FG,
} from '../../../../config/exceptions/shared/issuesState'

export interface ObjectActionLineProps {
  /** Open, non-umbrella issue count - drives the button labels. */
  actionableCount: number
  /** When false, "Can't fix yet" is disabled (every open row is already blocked). */
  hasBlockable: boolean
  pending: boolean
  onFixAll: () => void
  onCantFix: () => void
}

// Shared outlined-button look: neutral chrome + a light hairline border, the
// icon previews the outcome (teal check / red pause).
const actionButtonSx = {
  textTransform: 'none' as const,
  fontSize: '13px',
  fontWeight: 600,
  color: poolwerxColors.text.primary,
  border: `0.5px solid ${poolwerxColors.border.secondary}`,
  borderRadius: 1,
  px: '12px',
  py: '5px',
  whiteSpace: 'nowrap' as const,
  '&:hover': { border: `0.5px solid ${poolwerxColors.border.secondary}` },
  '& .MuiButton-startIcon': { mr: '6px' },
}

const ObjectActionLine: React.FC<ObjectActionLineProps> = ({
  actionableCount,
  hasBlockable,
  pending,
  onFixAll,
  onCantFix,
}) => {
  const single = actionableCount === 1

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        alignItems: 'center',
        // Left-aligned, on its own slim row directly beneath the tabs, with a
        // hairline divider closing it off from the body below.
        justifyContent: 'flex-start',
        px: '22px',
        py: '12px',
        bgcolor: 'background.paper',
        borderBottom: `0.5px solid ${poolwerxColors.border.tertiary}`,
      }}
    >
      <Button
        size="small"
        variant="outlined"
        disabled={pending || actionableCount === 0}
        onClick={onFixAll}
        startIcon={
          <AssignmentTurnedInOutlinedIcon
            sx={{ fontSize: 16, color: MARKED_FIXED_FG }}
          />
        }
        sx={actionButtonSx}
      >
        {single ? 'Fixed this' : `Fixed all ${actionableCount}`}
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={pending || !hasBlockable}
        onClick={onCantFix}
        startIcon={
          <PauseCircleOutlineIcon sx={{ fontSize: 16, color: BLOCKED_FG }} />
        }
        sx={actionButtonSx}
      >
        Can't fix yet
      </Button>
    </Box>
  )
}

export default ObjectActionLine
